/**
 * Mobile Top Bar
 *
 * Slim top bar visible only on mobile (<1024px).
 * Contains hamburger menu, Crucio logo, and API status.
 */

import { Activity, Eye, Menu, Radio, Wifi, WifiOff } from "lucide-react";
import { Link } from "react-router";
import { useDataMode } from "../../providers/DataModeProvider";
import { AuthStatusSection } from "./AuthStatusSection";
import { cn } from "../../lib/utils";

interface MobileTopBarProps {
  onMenuToggle: () => void;
}

// Badge config derived from DataModeProvider's canonical badgeState.
const BADGE_CONFIG = {
  live: {
    icon: Wifi,
    label: "Live",
    color: "bg-accent-green/20 text-accent-green",
  },
  public: {
    icon: Eye,
    label: "Public",
    color: "bg-accent-cyan/20 text-accent-cyan",
  },
  offline: {
    icon: WifiOff,
    label: "Offline",
    color: "bg-accent-yellow/20 text-accent-yellow",
  },
  demo: {
    icon: Radio,
    label: "Demo",
    color: "bg-accent-purple/20 text-accent-purple",
  },
} as const;

export function MobileTopBar({ onMenuToggle }: MobileTopBarProps) {
  const { apiReachable, badgeState } = useDataMode();
  const badge = BADGE_CONFIG[badgeState];

  return (
    <div className="h-12 bg-bg-secondary border-b border-border flex items-center justify-between px-3 lg:hidden shrink-0">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuToggle}
          className="p-1 text-text-muted hover:text-text-primary transition-colors"
          aria-label="Toggle navigation"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent-orange" />
          <span className="text-sm font-semibold text-text-primary">
            Crucio
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-3">
        <span
          className={cn(
            "w-2 h-2 rounded-full",
            apiReachable ? "bg-accent-green" : "bg-accent-red",
          )}
        />
        <span
          className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium",
            badge.color,
          )}
        >
          <badge.icon className="w-3 h-3" />
          {badge.label}
        </span>
        <AuthStatusSection isExpanded={false} />
      </div>
    </div>
  );
}
